import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Mp0051MasterDataResponse } from './mp0051.entity';
import { ApiClientService } from '@xbh/common-lib';
import { DataCacheService } from '../../shared/data-save/data-cache.service';
import { parseYyyyMMddToDate } from '../../pages/common/utils/date-utils';
/**
 * マスタデータグローバルストア
 *
 * アプリ起動時にMP0051APIを呼び出し、全画面で共有するマスタデータを管理する。
 * providedIn: 'root' によりシングルトンとして動作し、ページ遷移後もデータが保持される。
 */
@Injectable({ providedIn: 'root' })
export class MasterDataStore {
  private readonly apiClient = inject(ApiClientService);

  /** マスタデータ本体（未取得時はnull） */
  private readonly _masterData = signal<Mp0051MasterDataResponse['masterData'] | null>(null);

  /** 読み取り専用のマスタデータ */
  readonly masterData = this._masterData.asReadonly();

  private readonly dataCacheService = inject(DataCacheService);

  /** データ取得済みフラグ */
  readonly isLoaded = computed(() => this._masterData() !== null);

  /**
   * マスタデータを取得してストアに保存する
   * アプリ起動時に一度だけ呼び出す
   */
  loadAsObservable(): Observable<any> {
    return this.getMasterData().pipe(
      tap((response) => {
        this._masterData.set(response.masterData);
      }),
    );
  }

  /**
   * メッセージマスタを取得する
   *
   * @returns メッセージマスタ（見つからない場合はnull）
   */
  getMessageMaster(): Mp0051MasterDataResponse['masterData']['messageMaster'] | null {
    return this._masterData()?.messageMaster ?? null;
  }

  /**
   * messageId に対応する messageMainText を取得する
   *
   * @param messageId 検索するメッセージID
   * @returns メッセージ本文（見つからない場合は空文字）
   */
  getMessageText(messageId: string): string {
    const message = this._masterData()?.messageMaster?.find((m) => m.messageId === messageId);
    return message?.messageMainText ?? '';
  }

  /**
   * messageId に対応する message情報 を取得する
   *
   * @param messageId 検索するメッセージID
   * @returns メッセージ情報（見つからない場合はundefined）
   */
  getMessageInfo(messageId: string) {
    const message = this._masterData()?.messageMaster?.find((m) => m.messageId === messageId);
    return message ?? null;
  }

  /**
   * 特別勘定マスタを取得する
   *
   * @returns 特別勘定マスタ（見つからない場合はnull）
   */
  getSeparateAccountMaster(): Mp0051MasterDataResponse['masterData']['separateAccounMaster'] | null {
    return this._masterData()?.separateAccounMaster ?? null;
  }

  /**
   * 特別勘定コードに対応する特別勘定情報を取得する
   *
   * @param code 特別勘定コード
   * @returns 特別勘定情報（見つからない場合はundefined）
   */
  getSeparateAccount(code: string) {
    return this._masterData()?.separateAccounMaster?.find((s) => s.separateAccountCode === code);
  }

  /**
   * 特別勘定マスタを取得する
   *
   * @returns 遷移先マスタ（見つからない場合はnull）
   */
  getDestinationMaster(): Mp0051MasterDataResponse['masterData']['destinationMaster'] | null {
    return this._masterData()?.destinationMaster ?? null;
  }

  /**
   * 遷移先コードに対応する遷移先の表示タイトルを取得する
   *
   * @param code 遷移先コード
   * @returns 遷移先の表示タイトル（見つからない場合は空文字）
   */
  getDisplayTitle(code: string) {
    return this._masterData()?.destinationMaster?.find((d) => d.transitionDestinationCode === code)?.displayTitle ?? '';
  }

  /**
   * 遷移先コードに対応する遷移先のURLを取得する
   *
   * @param code 遷移先コード
   * @returns 遷移先のURL（見つからない場合は'#'）
   */
  getTransitionDestinationUrl(code: string) {
    return this._masterData()?.destinationMaster?.find((d) => d.transitionDestinationCode === code)?.transitionDestinationUrl ?? '#';
  }

  /**
   * 用語マスタを取得する
   *
   * @returns 用語マスタ（見つからない場合はnull）
   */
  getWordMaster(): Mp0051MasterDataResponse['masterData']['wordMaster'] | null {
    return this._masterData()?.wordMaster ?? null;
  }

  /**
   * 用語IDに対応する用語名を取得する
   *
   * @param wordId 用語ID
   * @returns 用語名（見つからない場合は空文字）
   */
  getWordName(wordId: string) {
    return this._masterData()?.wordMaster?.find((w) => w.wordId === wordId)?.wordName ?? '';
  }

  /**
   * 用語IDに対応するタイトルを取得する
   *
   * @param wordId 用語ID
   * @returns タイトル（見つからない場合は空文字）
   */
  getWordTitle(wordId: string) {
    return this._masterData()?.wordMaster?.find((w) => w.wordId === wordId)?.wordTitle ?? '';
  }

  /**
   * 用語IDに対応する用語説明を取得する
   *
   * @param wordId 用語ID
   * @returns 用語説明（見つからない場合は空文字）
   */
  getWordExplanation(wordId: string) {
    return this._masterData()?.wordMaster?.find((w) => w.wordId === wordId)?.explanation ?? '';
  }

  /**
   * 推移グラフ設定マスタを取得する
   *
   * @returns 推移グラフ設定マスタ（見つからない場合はnull）
   */
  getChartMaster(): Mp0051MasterDataResponse['masterData']['chartMaster'] | null {
    return this._masterData()?.chartMaster ?? null;
  }

  /**
   * チャートIDに対応するチャート情報を取得する
   *
   * @param yearMonthType 年月タイプ
   * @returns チャート情報（見つからない場合はundefined）
   */
  getChart(yearMonthType: string) {
    return this._masterData()?.chartMaster?.find((c) => c.yearMonthType === yearMonthType);
  }

  /**
   * コードマスタを取得する
   *
   * @returns コードマスタ（見つからない場合はnull）
   */
  getCodeMaster(): Mp0051MasterDataResponse['masterData']['codeMaster'] | null {
    return this._masterData()?.codeMaster ?? null;
  }

  /**
   * コード分類とコード値に対応するコード名称を取得する
   *
   * @param codeClassification コード分類
   * @param codeValue コード値
   * @returns コード名称（見つからない場合は空文字）
   */
  getCodeName(codeClassification: string, codeValue: string) {
    return this._masterData()?.codeMaster?.find((c) => c.codeClassification === codeClassification && c.codeValue === codeValue)?.codeName ?? '';
  }

  /**
   * システム日付を取得する
   * <p>
   * マスタ未取得・不正値の場合はブラウザの当日日付を返す。
   * </p>
   *
   * @returns システム日付
   */
  getSystemDate(): Date {
    return parseYyyyMMddToDate(this._masterData()?.systemDate) ?? new Date();
  }

  /**
   * マスタデータ取得API (MP0051) を呼び出す（GET方式）
   */
  getMasterData(): Observable<Mp0051MasterDataResponse> {
    const url = `${environment.bffUrl}/mp/${environment.apiVersion}/api/mp0051`;
    return new Observable((observer) => {
      this.apiClient.request<Mp0051MasterDataResponse>(url, 'get', 'mp', 'MP_GROUP1', '').subscribe({
        next: (data) => {
          observer.next(data);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }
}
